/**
 * Common UI utilities for payment forms
 */

import {ELEMENT_IDS} from './PaymentSDKBase';
import {CustomPaymentMethod, TaxFormPrefill} from '../types';
import {PaymentLocale, t} from './I18nService';

export interface ToastOptions {
    duration?: number;
    position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
    className?: string;
}

export interface LoadingSpinnerStyles {
    width?: string;
    height?: string;
    borderWidth?: string;
    borderColor?: string;
    borderTopColor?: string;
    margin?: string;
}

export interface ContainerStyles {
    padding?: string;
    textAlign?: 'left' | 'center' | 'right';
    fontFamily?: string;
    background?: string;
    borderRadius?: string;
    border?: string;
}

/**
 * Check if a string is a valid base64 encoded string
 */
export function isBase64String(str: string | null | undefined): boolean {
    if (!str) {
        return false;
    }
    // Check if it's a valid base64 string format
    const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
    if (!base64Regex.test(str)) {
        return false;
    }
    try {
        // Try to decode the base64 string
        const decoded = atob(str);
        // Check if the decoded string matches the pattern: "tokenIdentifier timestamp"
        // where timestamp is a number (can be in seconds or milliseconds)
        const parts = decoded.split(' ');
        // Should have exactly 2 parts: tokenIdentifier and timestamp
        if (parts.length !== 2) {
            return false;
        }
        const [tokenIdentifier, timestampStr] = parts;
        // Token identifier should not be empty
        if (!tokenIdentifier || tokenIdentifier.trim().length === 0) {
            return false;
        }
        // Optionally, check if timestampStr is a valid number
        if (isNaN(Number(timestampStr))) {
            return false;
        }
        return true;
    } catch {
        return false;
    }
}

/**
 * Show a toast notification
 */
export function showToast(
    message: string,
    isError: boolean = false,
    options: ToastOptions = {}
): void {
    const {
        duration = 3000,
        position = 'top-right',
        className = ''
    } = options;

    const toast = document.createElement('div');
    toast.textContent = message;
    toast.className = `elsa-toast ${className}`;

    // Apply styles
    Object.assign(toast.style, {
        position: 'fixed',
        zIndex: '10000',
        backgroundColor: isError ? '#f44336' : '#4CAF50',
        color: 'white',
        padding: '15px 20px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        fontSize: '14px',
        fontWeight: '500',
        maxWidth: '400px',
        wordWrap: 'break-word',
        opacity: '0',
        transform: 'translateY(-20px)',
        transition: 'all 0.3s ease',
        ...getPositionStyles(position)
    });

    document.body.appendChild(toast);

    // Animate in
    requestAnimationFrame(() => {
        toast.style.opacity = '1';
        toast.style.transform = 'translateY(0)';
    });

    // Auto remove
    setTimeout(() => {
        toast.style.opacity = '0';
        toast.style.transform = 'translateY(-20px)';
        setTimeout(() => {
            if (document.body.contains(toast)) {
                document.body.removeChild(toast);
            }
        }, 300);
    }, duration);
}

/**
 * Get position styles for toast notifications
 */
function getPositionStyles(position: string): Record<string, string> {
    switch (position) {
        case 'top-right':
            return {top: '20px', right: '20px'};
        case 'top-left':
            return {top: '20px', left: '20px'};
        case 'bottom-right':
            return {bottom: '20px', right: '20px'};
        case 'bottom-left':
            return {bottom: '20px', left: '20px'};
        default:
            return {top: '20px', right: '20px'};
    }
}

/**
 * Get default container styles for payment forms
 */
export function getContainerStyles(customStyles: ContainerStyles = {}): Record<string, string> {
    return {
        padding: '20px',
        textAlign: 'left',
        fontFamily: 'Arial, sans-serif',
        background: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #dee2e6',
        ...customStyles
    };
}

/**
 * Get loading spinner styles
 */
export function getLoadingSpinnerStyles(customStyles: LoadingSpinnerStyles = {}): Record<string, string> {
    return {
        display: 'inline-block',
        width: '40px',
        height: '40px',
        border: '4px solid #f3f3f3',
        borderTop: '4px solid #007bff',
        borderRadius: '50%',
        animation: 'elsa-spin 1s linear infinite',
        margin: '20px auto',
        ...customStyles
    };
}

/**
 * Get loading container styles
 */
export function getLoadingContainerStyles(): Record<string, string> {
    return {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px',
        color: '#666'
    };
}

/**
 * Create and inject CSS styles for the payment SDK
 */
export function injectPaymentStyles(): void {
    if (document.getElementById('elsa-payment-styles')) {
        return; // Already injected
    }

    const style = document.createElement('style');
    style.id = 'elsa-payment-styles';
    style.textContent = `
    @keyframes elsa-spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .elsa-payment-sdk {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
    }

    .elsa-payment-form {
      margin: 0 auto;
    }

    .elsa-payment-total-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px;
      margin-bottom: 16px;
      background-color: #f8f9fa;
      border: 1px solid #e9ecef;
      border-radius: 8px;
    }

    .elsa-payment-total-label {
      font-size: 16px;
      font-weight: 600;
      color: #333;
    }

    .elsa-payment-total-value {
      display: inline-flex;
      align-items: baseline;
      gap: 8px;
    }

    .elsa-payment-total-original {
      color: #888;
      font-size: 14px;
      text-decoration: line-through;
    }

    .elsa-payment-total-amount {
      font-size: 18px;
      font-weight: 700;
      color: #111;
    }

    .elsa-payment-element {
      padding: 0px;
      margin: 0;
    }

    .elsa-submit-button {
      background-color: #007bff;
      color: white;
      border: none;
      border-radius: 8px;
      padding: 15px 20px;
      font-size: 16px;
      font-weight: 500;
      width: 100%;
      cursor: pointer;
      transition: background-color 0.2s ease;
    }

    .elsa-submit-button:hover:not(:disabled) {
      background-color: #0056b3;
    }

    .elsa-submit-button:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .elsa-error-message {
      color: #dc3545;
      padding: 15px;
      background-color: #f8d7da;
      border: 1px solid #f5c6cb;
      border-radius: 8px;
      text-align: center;
      margin: 10px 0;
    }

    .elsa-payment-method-hidden {
      display: none !important;
    }

    .elsa-fundiin-phone-container {
      padding-top: 10px;
      margin-top: 10px;
      border-top: 1px solid #e0e0e0;
    }

    .elsa-phone-label {
      display: block;
      margin-bottom: 8px;
      font-weight: 500;
      font-size: 14px;
      color: #333;
    }

    .elsa-required {
      color: #dc3545;
      font-weight: bold;
    }

    .elsa-phone-input {
      width: 100%;
      padding: 10px 12px;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      font-size: 14px;
      transition: border-color 0.2s ease;
      box-sizing: border-box;
    }

    .elsa-phone-input:focus {
      outline: none;
      border-color: #007bff;
      box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
    }

    .elsa-phone-input:required:invalid {
      border-color: #dc3545;
    }
    `;

    document.head.appendChild(style);
}

/**
 * Create a loading state HTML structure
 */
export function createLoadingHTML(message?: string, locale: PaymentLocale = 'en'): string {
    const displayMessage = message || t(locale, 'loadingPaymentForm');
    return `
    <div class="elsa-loading-container" style="${Object.entries(getLoadingContainerStyles()).map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`).join('; ')}">
      <div class="elsa-loading-spinner" style="${Object.entries(getLoadingSpinnerStyles()).map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`).join('; ')}"></div>
      <p style="margin-top: 16px; font-size: 16px;">${displayMessage}</p>
    </div>
  `;
}

/**
 * Create payment form HTML structure
 */
export function createPaymentFormHTML(locale: PaymentLocale = 'en'): string {
    return `
    <div class="elsa-payment-form">
      <div id="${ELEMENT_IDS.paymentElement}" class="elsa-payment-element">
        <!-- Payment element will be mounted here -->
      </div>
      <button type="submit" id="${ELEMENT_IDS.submitButton}" class="elsa-submit-button">
        ${t(locale, 'payNow')}
      </button>
      <div id="${ELEMENT_IDS.messageContainer}" class="elsa-payment-message" style="display: none;"></div>
    </div>
  `;
}

/**
 * Show loading state on submit button
 */
export function setSubmitButtonLoading(isLoading: boolean, buttonId?: string, locale: PaymentLocale = 'en'): void {
    const actualButtonId = buttonId || ELEMENT_IDS.submitButton;
    const button = document.getElementById(actualButtonId) as HTMLButtonElement;
    if (!button) return;

    if (isLoading) {
        button.disabled = true;
        button.innerHTML = `<span class="elsa-loading-spinner"></span>${t(locale, 'processing')}`;
    } else {
        button.disabled = false;
        button.innerHTML = t(locale, 'payNow');
    }
}

/**
 * Update submit button text based on payment method
 */
export function updateSubmitButtonText(paymentMethod: string, buttonId?: string, locale: PaymentLocale = 'en'): void {
    const actualButtonId = buttonId || ELEMENT_IDS.submitButton;
    const button = document.getElementById(actualButtonId) as HTMLButtonElement;
    if (!button) return;

    // Don't update if button is in loading state
    if (button.disabled) return;

    let buttonText = t(locale, 'payNow'); // Default text for Stripe

    if (paymentMethod != 'stripe_js') {
        buttonText = t(locale, 'continue');
    }

    button.innerHTML = buttonText;
}

/**
 * Show error or success message
 */
export function showPaymentMessage(
    message: string,
    isError: boolean = false,
    containerId?: string
): void {
    const actualContainerId = containerId || ELEMENT_IDS.messageContainer;
    const messageContainer = document.getElementById(actualContainerId);
    if (!messageContainer) return;

    messageContainer.textContent = message;
    messageContainer.className = `elsa-payment-message ${isError ? 'elsa-error-message' : 'elsa-success-message'}`;
    messageContainer.style.display = 'block';
}

/**
 * Custom payment method styles
 */
export const CUSTOM_PAYMENT_STYLES = {
    methodsContainer: {
        marginBottom: '24px',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        overflow: 'hidden',
        backgroundColor: '#ffffff'
    },

    methodOption: {
        display: 'flex',
        alignItems: 'center',
        padding: '16px',
        borderBottom: '1px solid #e0e0e0',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
        backgroundColor: '#ffffff'
    },

    methodOptionSelected: {
        backgroundColor: '#e3f2fd',
        borderLeft: '4px solid #007bff'
    },

    radio: {
        width: '20px',
        height: '20px',
        marginRight: '12px',
        cursor: 'pointer',
        accentColor: '#007bff'
    },

    methodIcon: {
        fontSize: '24px',
        marginRight: '12px',
        lineHeight: '1'
    },

    methodContent: {
        flex: '1'
    },

    methodLabel: {
        fontSize: '16px',
        fontWeight: '500',
        color: '#333',
        marginBottom: '0',
        display: 'block'
    },

    methodDescription: {
        fontSize: '13px',
        color: '#666',
        marginTop: '4px',
        display: 'block'
    }
};

/**
 * Create custom payment methods HTML section
 */
export function createCustomPaymentMethodsHTML(
    methods: CustomPaymentMethod[],
    selectedMethod: string = 'stripe_js',
    defaultPhone: string = '',
    locale: PaymentLocale = 'en'
): string {
    if (!methods || methods.length === 0) {
        return '';
    }

    // If only Stripe is available, render simple form without radio buttons
    if (methods.length === 1 && methods[0].id === 'stripe_js') {
        return `
        <div id="${ELEMENT_IDS.paymentElement}" class="elsa-payment-element">
            <!-- Payment element will be mounted here -->
        </div>
        `;
    }

    // Multiple payment methods - render all methods with radio buttons
    let methodsHTML = '';

    // Render all payment methods equally
    methods.forEach(method => {
        const isSelected = method.id === selectedMethod;
        const selectedClass = isSelected ? 'elsa-payment-method-selected' : '';

        // Special handling for Stripe to include the embedded payment element
        if (method.id === 'stripe_js') {
            const stripeDisplayStyle = isSelected ? 'display: block;' : 'display: none;';

            methodsHTML += `
            <div class="elsa-payment-method-option ${selectedClass}" data-method-id="${method.id}">
                <input
                    type="radio"
                    id="${ELEMENT_IDS.paymentMethodRadio(method.id)}"
                    name="payment-method"
                    value="${method.id}"
                    ${isSelected ? 'checked' : ''}
                    class="elsa-payment-method-radio"
                />
                ${method.icon ? `<span class="elsa-payment-method-icon">${method.icon}</span>` : ''}
                <div class="elsa-payment-method-wrapper">
                    <label for="${ELEMENT_IDS.paymentMethodRadio(method.id)}" class="elsa-payment-method-content">
                        <span class="elsa-payment-method-label">${method.label}</span>
                        ${method.description ? `<span class="elsa-payment-method-description">${method.description}</span>` : ''}
                    </label>
                </div>
            </div>
            <!-- Stripe Element Container placed right after Stripe radio -->
            <div id="${ELEMENT_IDS.stripeElementContainer}" class="elsa-stripe-element-container-inline" style="${stripeDisplayStyle}">
                <div id="${ELEMENT_IDS.paymentElement}" class="elsa-payment-element">
                    <!-- Payment element will be mounted here -->
                </div>
            </div>
        `;
        } else {
            // Render other payment methods
            const phoneValue = (defaultPhone || '').replace(/"/g, '&quot;');
            const fundiiPhoneHTML = `
                <div id="fundiin-phone-container" class="elsa-fundiin-phone-container" style="display: ${selectedMethod === 'fundiin' ? 'block' : 'none'};">
                    <label for="fundiin-phone-input" class="elsa-phone-label">
                        ${t(locale, 'phoneNumber')} <span class="elsa-required">*</span>
                    </label>
                    <input
                        type="tel"
                        id="fundiin-phone-input"
                        name="fundiin-phone"
                        class="elsa-phone-input"
                        placeholder="${t(locale, 'enterPhoneNumber')}"
                        value="${phoneValue}"
                        required />
                </div>
            `;
            methodsHTML += `
            <div class="elsa-payment-method-option ${selectedClass}" data-method-id="${method.id}">
                <input
                    type="radio"
                    id="${ELEMENT_IDS.paymentMethodRadio(method.id)}"
                    name="payment-method"
                    value="${method.id}"
                    ${isSelected ? 'checked' : ''}
                    class="elsa-payment-method-radio"
                />
                ${method.icon ? `<span class="elsa-payment-method-icon">${method.icon}</span>` : ''}
                <div class="elsa-payment-method-wrapper">
                    <label for="${ELEMENT_IDS.paymentMethodRadio(method.id)}" class="elsa-payment-method-content">
                        <span class="elsa-payment-method-label">${method.label}</span>
                        ${method.description ? `<span class="elsa-payment-method-description">${method.description}</span>` : ''}
                    </label>
                    ${method.id === 'fundiin' ? fundiiPhoneHTML : ''}
                </div>
            </div>
        `;
        }
    });

    return `
        <div id="${ELEMENT_IDS.customPaymentMethods}" class="elsa-custom-payment-methods">
            ${methodsHTML}
        </div>
    `;
}

/**
 * Format amount with currency symbol using Intl.NumberFormat.
 * Falls back to plain "amount CURRENCY" if locale formatting is unavailable.
 */
export function formatCurrency(amount: number, currency: string, locale: PaymentLocale = 'en'): string {
    const upper = currency.toUpperCase();
    try {
        const formatted = new Intl.NumberFormat(locale === 'vi' ? 'vi-VN' : 'en-US', {
            style: 'currency',
            currency: upper,
            minimumFractionDigits: upper === 'VND' || upper === 'KRW' || upper === 'JPY' ? 0 : 2,
            maximumFractionDigits: upper === 'VND' || upper === 'KRW' || upper === 'JPY' ? 0 : 2,
        }).format(amount);
        return `${formatted} ${upper}`;
    } catch {
        return `${amount} ${upper}`;
    }
}

/**
 * VN VAT / e-invoice block (VND checkout only). Checkbox toggles fields (disabled until checked).
 * Prefill is stored on `data-tax-prefill` and applied when the user checks the box (see PaymentManager).
 */
export function createTaxFormHTML(
    locale: PaymentLocale = 'en',
    show: boolean = false,
    prefill: TaxFormPrefill = {}
): string {
    if (!show) {
        return '';
    }
    const esc = (s: string) =>
        s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
    const prefillPayload = encodeURIComponent(JSON.stringify(prefill));
    return `
    <div class="elsa-tax-section" data-tax-prefill="${prefillPayload}">
      <label class="elsa-tax-checkbox-label">
        <input type="checkbox" id="${ELEMENT_IDS.taxCheckbox}" class="elsa-tax-checkbox-input" />
        <span class="elsa-tax-checkbox-text">${esc(t(locale, 'taxInvoiceTitle'))}</span>
      </label>
      <div id="${ELEMENT_IDS.taxFormContainer}" class="elsa-tax-form-collapsible" style="display: none;">
        <p class="elsa-tax-intro">${esc(t(locale, 'taxInvoiceIntro'))}</p>
        <div class="elsa-tax-field">
          <label class="elsa-tax-label" for="${ELEMENT_IDS.taxNumber}">${esc(t(locale, 'taxNumber'))}</label>
          <input type="text" id="${ELEMENT_IDS.taxNumber}" class="elsa-tax-input" autocomplete="off" disabled placeholder="${esc(t(locale, 'taxNumber'))}" />
        </div>
        <div class="elsa-tax-field">
          <label class="elsa-tax-label" for="${ELEMENT_IDS.taxCompany}">${esc(t(locale, 'taxCompanyName'))}</label>
          <input type="text" id="${ELEMENT_IDS.taxCompany}" class="elsa-tax-input" autocomplete="organization" disabled placeholder="${esc(t(locale, 'taxCompanyName'))}" />
        </div>
        <div class="elsa-tax-field">
          <label class="elsa-tax-label" for="${ELEMENT_IDS.taxBuyerName}">${esc(t(locale, 'taxBuyerName'))}</label>
          <input type="text" id="${ELEMENT_IDS.taxBuyerName}" class="elsa-tax-input" autocomplete="name" disabled placeholder="${esc(t(locale, 'taxBuyerName'))}" />
        </div>
        <div class="elsa-tax-field">
          <label class="elsa-tax-label" for="${ELEMENT_IDS.taxAddress}">${esc(t(locale, 'taxCompanyAddress'))}</label>
          <textarea id="${ELEMENT_IDS.taxAddress}" class="elsa-tax-textarea" rows="3" autocomplete="street-address" disabled placeholder="${esc(t(locale, 'taxCompanyAddress'))}"></textarea>
        </div>
        <div class="elsa-tax-field">
          <label class="elsa-tax-label" for="${ELEMENT_IDS.taxEmail}">${esc(t(locale, 'taxInvoiceEmail'))}</label>
          <input type="email" id="${ELEMENT_IDS.taxEmail}" class="elsa-tax-input" autocomplete="email" disabled placeholder="${esc(t(locale, 'taxInvoiceEmail'))}" />
        </div>
        <div id="${ELEMENT_IDS.taxError}" class="elsa-tax-error-msg" role="alert" style="display: none;"></div>
        <p class="elsa-tax-note">${esc(t(locale, 'taxInvoiceNote'))}</p>
      </div>
    </div>`;
}

/**
 * Create enhanced payment form with custom methods
 */
export function createEnhancedPaymentFormHTML(
    customMethods?: CustomPaymentMethod[],
    selectedMethod: string = 'stripe_js',
    paymentType: string = 'onetime',
    isTrial: boolean = false,
    nextBillingDate: number = 0,
    amount: number = 0,
    currency: string = '',
    promotionCode: string = '',
    hasPromotionToken: boolean = false,
    isUpgrade: boolean = false,
    grantOption: 'auto' | 'voucher' = 'auto',
    _defaultEmail: string = '',
    defaultPhone: string = '',
    locale: PaymentLocale = 'en',
    showVnTaxForm: boolean = false,
    taxFormPrefill: TaxFormPrefill = {},
    beforeDiscountAmount: number = 0
): string {
    const hasCustomMethods = customMethods && customMethods.length > 0;
    console.log('beforeDiscountAmount', beforeDiscountAmount);

    // Get all payment methods as radio options with Stripe Element embedded
    const customMethodsHTML = hasCustomMethods ? createCustomPaymentMethodsHTML(customMethods, selectedMethod, defaultPhone, locale) : '';
    const formattedAmount = (amount && currency) ? formatCurrency(amount, currency, locale) : '';
    const hasDiscount = beforeDiscountAmount > 0 && beforeDiscountAmount > amount && !!currency;
    const formattedBeforeDiscount = hasDiscount ? formatCurrency(beforeDiscountAmount, currency, locale) : '';
    const totalHeaderHTML = formattedAmount
        ? `
      <div class="elsa-payment-total-row">
        <span class="elsa-payment-total-label">${t(locale, 'total')}</span>
        <span class="elsa-payment-total-value">
          ${hasDiscount ? `<span class="elsa-payment-total-original">${formattedBeforeDiscount}</span>` : ''}
          <span class="elsa-payment-total-amount">${formattedAmount}</span>
        </span>
      </div>`
        : '';
    let warningContent = '';
    if (paymentType === 'subscription') {
        let formatted = '[date/month/year]';
        if (nextBillingDate && Number.isFinite(nextBillingDate) && nextBillingDate > 0) {
            const endDate = new Date(nextBillingDate * 1000);
            const dd = String(endDate.getDate()).padStart(2, '0');
            const mm = String(endDate.getMonth() + 1).padStart(2, '0');
            const yyyy = String(endDate.getFullYear());
            formatted = `${dd}/${mm}/${yyyy}`;
        }
        const amountText = formattedAmount ? ` <strong>${formattedAmount}</strong>` : '';
        const instructionLink = '<a href="https://elsanow.freshdesk.com/en/support/solutions/articles/31000176413-cancel-for-web-subscriptions-from-elsaspeak-com-" target="_blank" rel="noopener noreferrer">this page</a>';
        if (isTrial) {
            const trialPeriodDays = Math.ceil((nextBillingDate! * 1000 - Date.now()) / (1000 * 60 * 60 * 24));
            warningContent = t(locale, 'subscriptionTrialWarning', {amountText, trialPeriodDays, formattedDate: formatted, instructionLink});
        } else {
            warningContent = t(locale, 'subscriptionRenewalWarning', {amountText, formattedDate: formatted, instructionLink});
        }
    } else if (paymentType === 'onetime' && formattedAmount) {
        warningContent = t(locale, 'onetimeChargeWarning', {amountText: `<strong>${formattedAmount}</strong>`});
    }

    const grantNoteText = grantOption === 'voucher'
        ? t(locale, 'grantVoucher')
        : t(locale, 'grantAuto');

    const warningHTML = warningContent
        ? `<div class="trial-warning">${warningContent} ${grantNoteText}</div>`
        : `<div class="trial-warning">${grantNoteText}</div>`;

    const taxFormHTML = createTaxFormHTML(locale, showVnTaxForm, taxFormPrefill);

    let promotionHTML = '';
    if (hasPromotionToken && promotionCode) {
        promotionHTML = `<p class="elsa-promotion-banner" id="${ELEMENT_IDS.promotionContainer}">🎉 ${t(locale, 'promotionApplied', {promotionCode: `<strong>${promotionCode}</strong>`})}</p>`;
    } else if (!hasPromotionToken && !isUpgrade && promotionCode) {
        promotionHTML = `<p class="elsa-promotion-banner" id="${ELEMENT_IDS.promotionContainer}">🎉 ${t(locale, 'promotionApplied', {promotionCode: `<strong>${promotionCode}</strong>`})} <button type="button" id="${ELEMENT_IDS.promotionRemoveBtn}" class="elsa-promotion-remove-btn">${t(locale, 'remove')}</button></p>`;
    } else if (!hasPromotionToken && !isUpgrade) {
        promotionHTML = `
            <div class="elsa-promotion-input-container" id="${ELEMENT_IDS.promotionContainer}">
                <input type="text" id="${ELEMENT_IDS.promotionInput}" class="elsa-promotion-input" placeholder="${t(locale, 'enterPromotionCode')}" />
                <button type="button" id="${ELEMENT_IDS.promotionApplyBtn}" class="elsa-promotion-apply-btn">${t(locale, 'apply')}</button>
            </div>`;
    }

    return `
    <div class="elsa-payment-form">
      ${totalHeaderHTML}
      ${promotionHTML}
      <!-- All Payment Methods as Radio Options with Stripe Element embedded -->
      ${customMethodsHTML}
      ${taxFormHTML}
      ${warningHTML}
      <button type="submit" id="${ELEMENT_IDS.submitButton}" class="elsa-submit-button">
        ${t(locale, 'payNow')}
      </button>
      <div id="${ELEMENT_IDS.messageContainer}" class="elsa-payment-message" style="display: none;"></div>
    </div>
  `;
}

/**
 * Toggle Stripe element container visibility
 */
export function toggleStripeElementVisibility(visible: boolean): void {
    const container = document.getElementById(ELEMENT_IDS.stripeElementContainer);
    if (!container) return;

    if (visible) {
        container.style.display = 'block';
        container.style.opacity = '0';
        setTimeout(() => {
            container.style.transition = 'opacity 0.3s ease';
            container.style.opacity = '1';
        }, 10);
    } else {
        container.style.transition = 'opacity 0.3s ease';
        container.style.opacity = '0';
        setTimeout(() => {
            container.style.display = 'none';
        }, 300);
    }
}

/**
 * Get selected payment method from radio buttons
 */
export function getSelectedPaymentMethod(): string | null {
    const radios = document.querySelectorAll('input[name="payment-method"]');
    for (const radio of radios) {
        if ((radio as HTMLInputElement).checked) {
            return (radio as HTMLInputElement).value;
        }
    }
    return null;
}

/**
 * Inject custom payment methods CSS
 */
export function injectCustomPaymentStyles(): void {
    if (document.getElementById('elsa-custom-payment-styles')) {
        return; // Already injected
    }

    const style = document.createElement('style');
    style.id = 'elsa-custom-payment-styles';
    style.textContent = `
    .elsa-payment-method-header {
      margin-bottom: 16px;
    }

    .elsa-payment-method-title {
      font-size: 18px;
      font-weight: 600;
      color: #333;
      margin: 0 0 12px 0;
      padding: 12px 16px;
      background-color: #f8f9fa;
      border: 1px solid #e9ecef;
      border-radius: 6px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      text-shadow: 0 1px 2px rgba(0,0,0,0.1);
    }

    .elsa-custom-payment-methods {
      margin-bottom: 24px;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      overflow: hidden;
      background-color: #ffffff;
    }

    .elsa-payment-method-option {
      display: flex;
      align-items: center;
      padding: 16px;
      border-bottom: 1px solid #e0e0e0;
      cursor: pointer;
      transition: all 0.3s ease;
      background-color: #ffffff;
      border-radius: 0;
    }

    .elsa-payment-method-option:first-child {
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;
    }

    .elsa-payment-method-option:last-child {
      border-bottom: none;
      border-bottom-left-radius: 8px;
      border-bottom-right-radius: 8px;
    }

    /* Hover state - light blue background */
    .elsa-payment-method-option:hover {
      background-color: #f0f8ff;
      background: linear-gradient(135deg, #f0f8ff 0%, #e6f3ff 100%);
      border-left: 3px solid #007bff;
      padding-left: 13px;
      transform: translateX(2px);
      box-shadow: 0 2px 8px rgba(0, 123, 255, 0.1);
    }

    /* Selected state - stronger blue background */
    .elsa-payment-method-option.elsa-payment-method-selected {
      background-color: #e3f2fd;
      background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
      border-left: 4px solid #007bff;
      padding-left: 12px;
      box-shadow: 0 2px 12px rgba(0, 123, 255, 0.15);
    }

    /* Selected state hover - even stronger highlight */
    .elsa-payment-method-option.elsa-payment-method-selected:hover {
      background: linear-gradient(135deg, #d1e7fd 0%, #a5d4fb 100%);
      border-left: 5px solid #0056b3;
      padding-left: 11px;
      transform: translateX(3px);
      box-shadow: 0 4px 16px rgba(0, 123, 255, 0.2);
    }

    .elsa-payment-method-radio {
      width: 16px;
      height: 16px;
      margin-right: 12px;
      cursor: pointer;
      accent-color: #007bff;
    }

    .elsa-payment-method-icon {
      font-size: 24px;
      margin-right: 12px;
      line-height: 1;
    }

    .elsa-payment-method-wrapper {
      flex: 1;
    }

    .elsa-payment-method-content {
      display: block;
      cursor: pointer;
      margin: 0;
    }

    .elsa-payment-method-label {
      font-size: 16px;
      font-weight: 500;
      color: #333;
      margin-bottom: 0;
      display: block;
    }

    .elsa-payment-method-description {
      font-size: 13px;
      color: #666;
      margin-top: 4px;
      display: block;
    }

    .elsa-stripe-element-container {
      transition: opacity 0.3s ease;
      margin-bottom: 24px;
    }

    .elsa-stripe-element-container-inline {
      padding: 5px;
      margin: 0;
      background-color: #ffffff;
      border-top: 1px solid #e0e0e0;
      transition: opacity 0.3s ease;
    }

    /* Remove excessive min-height that was causing bottom spacing */
    .elsa-payment-element iframe {
      min-height: 0 !important;
    }

    /* Increase spacing and size for better logo visibility */
    #payment-element,
    .elsa-payment-element {
      font-size: 16px !important;
    }
    .elsa-promotion-banner {
        color: #155724;
        background-color: #d4edda;
        border: 1px solid #c3e6cb;
        padding: 10px;
        border-radius: 6px;
        margin: 0 0 16px;
        font-size: 14px;
    }
    .elsa-promotion-remove-btn {
        background: none;
        border: none;
        color: #c82333;
        margin-left: 8px;
        font-size: 13px;
        text-decoration: underline;
        cursor: pointer;
        padding: 0;
        font-family: inherit;
    }
    .elsa-promotion-remove-btn:hover {
        color: #a71d2a;
    }
    .elsa-promotion-remove-btn:disabled {
        color: #999;
        cursor: not-allowed;
        text-decoration: none;
    }
    .elsa-promotion-input-container {
        display: flex;
        gap: 8px;
        margin: 0 0 16px;
    }
    .elsa-promotion-input {
        flex: 1;
        padding: 8px 12px;
        border: 1px solid #ced4da;
        border-radius: 6px;
        font-size: 14px;
        outline: none;
        transition: border-color 0.15s ease;
    }
    .elsa-promotion-input:focus {
        border-color: #635bff;
        box-shadow: 0 0 0 2px rgba(99, 91, 255, 0.15);
    }
    .elsa-promotion-apply-btn {
        padding: 8px 16px;
        background-color: #635bff;
        color: #fff;
        border: none;
        border-radius: 6px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: background-color 0.15s ease;
        white-space: nowrap;
    }
    .elsa-promotion-apply-btn:hover {
        background-color: #4b45c6;
    }
    .elsa-promotion-apply-btn:disabled {
        background-color: #a0a0a0;
        cursor: not-allowed;
    }
    .trial-warning {
        color: #856404;
        background-color: #fff3cd;
        border: 1px solid #ffeeba;
        padding: 10px;
        border-radius: 6px;
        margin: 10px 0 16px;
        font-size: 14px;
    }
    .trial-warning a {
        color: #0056b3;
    }

    .elsa-tax-section {
        margin: 0 0 20px;
        padding: 16px;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        background: #fff;
    }
    .elsa-tax-checkbox-label {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        cursor: pointer;
        font-weight: 600;
        font-size: 15px;
        color: #333;
        margin: 0;
    }
    .elsa-tax-checkbox-input {
        margin-top: 3px;
        width: 18px;
        height: 18px;
        flex-shrink: 0;
        accent-color: #007bff;
    }
    .elsa-tax-form-collapsible {
        margin-top: 14px;
        padding-top: 14px;
        border-top: 1px solid #eee;
    }
    .elsa-tax-intro {
        font-size: 13px;
        font-weight: 600;
        color: #444;
        margin: 0 0 14px;
        line-height: 1.4;
    }
    .elsa-tax-field {
        margin-bottom: 12px;
    }
    .elsa-tax-label {
        display: block;
        font-size: 13px;
        font-weight: 500;
        color: #333;
        margin-bottom: 6px;
    }
    .elsa-tax-input, .elsa-tax-textarea {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid #ced4da;
        border-radius: 6px;
        font-size: 14px;
        box-sizing: border-box;
        font-family: inherit;
    }
    .elsa-tax-input:focus, .elsa-tax-textarea:focus {
        outline: none;
        border-color: #007bff;
        box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.15);
    }
    .elsa-tax-input:disabled, .elsa-tax-textarea:disabled {
        background: #f5f5f5;
        color: #888;
    }
    .elsa-tax-error-msg {
        color: #dc3545;
        font-size: 13px;
        margin: 8px 0;
        padding: 8px 10px;
        background: #f8d7da;
        border: 1px solid #f5c6cb;
        border-radius: 6px;
    }
    .elsa-tax-note {
        font-size: 12px;
        color: #555;
        line-height: 1.5;
        margin: 12px 0 0;
    }
    `;

    document.head.appendChild(style);
}
